/**
 * Settings - Landing Page with Tab Navigation (regrouped DC-1645)
 *
 * True configuration + cross-cutting primitives only:
 * - Domains (cross-cutting scope primitive, moved from Governance)
 * - Integrations (Admin only - DC-1157)
 * - AI Preferences (moved from Governance)
 * - Access & Security cluster (Admin only): User Management, Audit Logs,
 *   Security, API Keys, Sessions
 *
 * Moved out by DC-1645: Team Management → People (/settings/team, rail
 * destination); Governance → its own rail destination; Private Ontologies →
 * the rail's Ontology destination (/ontology) — removed from Settings
 * altogether per operator decision 2026-06-11.
 *
 * @version 2.0.0
 * @date 2026-06-11
 */

import React from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  Boxes,
  Cable,
  Sparkles,
  Target,
  UserCog,
  ScrollText,
  Shield,
  KeyRound,
  MonitorSmartphone,
  Compass,
} from 'lucide-react';
import { BRANDING } from '../../config/branding';
import { UserDropdown } from '../../components/auth/UserDropdown';
import { useAuth } from '../../hooks/useAuth';
import './settings.css';

interface SettingsTab {
  to: string;
  label: string;
  description: string;
  adminOnly: boolean;
  icon: React.ReactNode;
}

const CONFIG_TABS: SettingsTab[] = [
  {
    // CC-V35-BANK-DEMO-READY-01: organization-wide workflow starting point
    // (AI use case–driven vs dataset-driven). Admin-only per the packet.
    to: '/settings/workflow',
    label: 'Organization Workflow',
    description:
      'Choose whether your organization starts from AI use cases or from datasets',
    adminOnly: true,
    icon: <Compass size={16} aria-hidden="true" />,
  },
  {
    to: '/settings/domains',
    label: 'Domains',
    description:
      'Define the business areas that scope triangulation, interviews, documents, and datasets',
    adminOnly: false,
    icon: <Boxes size={16} aria-hidden="true" />,
  },
  {
    to: '/settings/integrations',
    label: 'Integrations',
    description: 'Connect to external systems like Databricks, Collibra, and Veeva for data access',
    adminOnly: true,
    icon: <Cable size={16} aria-hidden="true" />,
  },
  {
    to: '/settings/ai-preferences',
    label: 'AI Preferences',
    description: 'Configure how Bridge AI analyzes and recommends across your organization',
    adminOnly: false,
    icon: <Sparkles size={16} aria-hidden="true" />,
  },
  {
    to: '/settings/pack-accuracy',
    label: 'Pack accuracy',
    description: 'Override telemetry per Domain Pack',
    adminOnly: true,
    icon: <Target size={16} aria-hidden="true" />,
  },
];

const ACCESS_TABS: SettingsTab[] = [
  {
    to: '/settings/users',
    label: 'User Management',
    description: 'Manage user accounts, roles, and permissions for your organization',
    adminOnly: true,
    icon: <UserCog size={16} aria-hidden="true" />,
  },
  {
    to: '/settings/audit-logs',
    label: 'Audit Logs',
    description: 'View and export security and activity logs for SOC2 compliance',
    adminOnly: true,
    icon: <ScrollText size={16} aria-hidden="true" />,
  },
  {
    to: '/settings/security',
    label: 'Security',
    description: 'Monitor security metrics, alerts, compliance status, and activity patterns',
    adminOnly: true,
    icon: <Shield size={16} aria-hidden="true" />,
  },
  {
    to: '/settings/api-keys',
    label: 'API Keys',
    description: `Create and manage API keys for programmatic access to ${BRANDING.productName}`,
    adminOnly: true,
    icon: <KeyRound size={16} aria-hidden="true" />,
  },
  {
    to: '/settings/sessions',
    label: 'Sessions',
    description: 'View and manage active user sessions across your organization',
    adminOnly: true,
    icon: <MonitorSmartphone size={16} aria-hidden="true" />,
  },
];

export const Settings: React.FC = () => {
  const location = useLocation();
  const { hasRole } = useAuth();
  const isAdmin = hasRole('admin');

  // Determine if we're at the root /settings path (no sub-route)
  const isRootPath = location.pathname === '/settings' || location.pathname === '/settings/';

  const visibleConfigTabs = CONFIG_TABS.filter((t) => !t.adminOnly || isAdmin);
  const visibleAccessTabs = ACCESS_TABS.filter((t) => !t.adminOnly || isAdmin);

  return (
    <div className="settings-page">
      {/* Header */}
      <div className="settings-header">
        <div className="settings-header-content">
          <h1>Settings</h1>
          <p className="settings-subtitle">
            Configure your organization&rsquo;s domains, integrations, AI preferences, and access
            controls
          </p>
        </div>
        <div className="settings-header-actions">
          <UserDropdown />
        </div>
      </div>

      {/* Tab Navigation */}
      <nav className="settings-tabs">
        {visibleConfigTabs.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            className={({ isActive }) => `settings-tab ${isActive ? 'active' : ''}`}
          >
            <span className="tab-icon">{tab.icon}</span>
            <span className="tab-label">{tab.label}</span>
          </NavLink>
        ))}
        {visibleAccessTabs.length > 0 && (
          <span className="settings-tabs-section" aria-hidden="true">
            Access &amp; Security
          </span>
        )}
        {visibleAccessTabs.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            className={({ isActive }) => `settings-tab ${isActive ? 'active' : ''}`}
          >
            <span className="tab-icon">{tab.icon}</span>
            <span className="tab-label">{tab.label}</span>
          </NavLink>
        ))}
      </nav>

      {/* Content Area */}
      <div className="settings-content">
        {isRootPath ? (
          <div className="settings-landing">
            <div className="settings-cards">
              {visibleConfigTabs.map((tab) => (
                <NavLink key={tab.to} to={tab.to} className="settings-card">
                  <div className="card-icon">{tab.icon}</div>
                  <h3>{tab.label}</h3>
                  <p>{tab.description}</p>
                </NavLink>
              ))}
            </div>
            {visibleAccessTabs.length > 0 && (
              <>
                <h2 className="settings-cards-section">Access &amp; Security</h2>
                <div className="settings-cards">
                  {visibleAccessTabs.map((tab) => (
                    <NavLink key={tab.to} to={tab.to} className="settings-card">
                      <div className="card-icon">{tab.icon}</div>
                      <h3>{tab.label}</h3>
                      <p>{tab.description}</p>
                    </NavLink>
                  ))}
                </div>
              </>
            )}
          </div>
        ) : (
          <Outlet />
        )}
      </div>
    </div>
  );
};

export default Settings;
